import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useStationManagementApi } from "../../hooks/useStationManagement";
import { Button, EMAIL_RE, Overlay, PHONE_RE, SelectField, TextField, Toggle } from "./ui";

const EMPTY = { group_name: "", owner_name: "", contact_email: "", contact_phone: "", status: "active", settlement_enabled: false };

const STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

export function validateGroup(form) {
  const errors = {};
  if (!form.group_name.trim()) errors.group_name = "Group name is required";
  else if (form.group_name.trim().length > 100) errors.group_name = "Keep it under 100 characters";
  if (form.owner_name.trim().length > 100) errors.owner_name = "Keep it under 100 characters";
  if (form.contact_email.trim() && !EMAIL_RE.test(form.contact_email.trim())) errors.contact_email = "Enter a valid email";
  if (form.contact_phone.trim() && !PHONE_RE.test(form.contact_phone.trim())) errors.contact_phone = "Enter a valid phone number";
  return errors;
}

// Create (group = null) or edit a group. onSaved receives the saved group from the server.
export default function GroupFormModal({ open, group, onClose, onSaved, layer }) {
  const api = useStationManagementApi();
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(group?.group_id);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setForm(group ? { ...EMPTY, ...pick(group) } : EMPTY);
  }, [open, group]);

  const onChange = (name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
    if (errors[name]) setErrors((e) => ({ ...e, [name]: undefined }));
  };

  const submit = async (e) => {
    e?.preventDefault();
    if (saving) return;
    const found = validateGroup(form);
    setErrors(found);
    if (Object.keys(found).length) return;

    setSaving(true);
    try {
      const payload = {
        ...form,
        group_name: form.group_name.trim(),
        owner_name: form.owner_name.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim(),
      };
      if (isEdit) payload.group_id = group.group_id;
      const saved = await api.saveGroup(payload);
      toast.success(isEdit ? `Group ${saved.group_id} updated` : `Group ${saved.group_id} · ${saved.group_name} created`);
      onSaved?.(saved);
      onClose();
    } catch (err) {
      toast.error(err.message || "Could not save the group");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Overlay
      open={open}
      onClose={saving ? undefined : onClose}
      layer={layer}
      title={isEdit ? `Edit group ${group.group_id}` : "Create group"}
      subtitle={isEdit ? group.group_name : "The group ID is assigned automatically when you save."}
      width="max-w-xl"
      footer={
        <>
          <Button onClick={onClose} disabled={saving}>Cancel</Button>
          <Button variant="primary" loading={saving} onClick={submit}>{isEdit ? "Save changes" : "Create group"}</Button>
        </>
      }
    >
      <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" noValidate>
        <TextField className="sm:col-span-2" label="Group name" name="group_name" required form={form} errors={errors} onChange={onChange} placeholder="e.g. Tetra Logistics" autoFocus maxLength={100} />
        <TextField label="Owner name" name="owner_name" form={form} errors={errors} onChange={onChange} maxLength={100} />
        <SelectField label="Status" name="status" form={form} errors={errors} onChange={onChange} options={STATUS_OPTIONS} />
        <TextField label="Contact email" name="contact_email" type="email" form={form} errors={errors} onChange={onChange} placeholder="ops@company.com" maxLength={100} />
        <TextField label="Contact phone" name="contact_phone" type="tel" form={form} errors={errors} onChange={onChange} placeholder="+91 98765 43210" maxLength={20} />
        <div className="sm:col-span-2">
          <Toggle
            label="Settlement enabled"
            description="Turns automatic settlement on or off for this group. Payout keys and fees are configured separately."
            checked={form.settlement_enabled}
            onChange={(v) => onChange("settlement_enabled", v)}
          />
        </div>
        <button type="submit" className="hidden" />
      </form>
    </Overlay>
  );
}

function pick(g) {
  return {
    group_name: g.group_name || "",
    owner_name: g.owner_name || "",
    contact_email: g.contact_email || "",
    contact_phone: g.contact_phone || "",
    status: g.status || "active",
    settlement_enabled: Boolean(g.settlement_enabled),
  };
}
